import { supabase } from '../lib/supabase';

/**
 * Upload event image to Supabase storage
 * @param {File} file - The image file to upload
 * @param {string} eventId - Event ID for file naming
 * @param {string} userId - User ID for folder organization
 * @returns {Promise<{url: string, path: string, error?: string}>}
 */
export const uploadEventImage = async (file, eventId, userId) => {
  try {
    // Generate unique filename
    const fileExt = file?.name?.split('.')?.pop()?.toLowerCase();
    const fileName = `${eventId}-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to storage
    const { data, error } = await supabase?.storage?.from('event-images')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) {
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase?.storage?.from('event-images')?.getPublicUrl(filePath);

    return {
      url: publicUrl,
      path: filePath,
      success: true
    };

  } catch (error) {
    console.error('Error uploading event image:', error);
    return {
      error: error?.message || 'Failed to upload image',
      success: false
    };
  }
};

/**
 * Delete event image from Supabase storage
 * @param {string} imagePath - Path to the image in storage
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteEventImage = async (imagePath) => {
  try {
    const { error } = await supabase?.storage?.from('event-images')?.remove([imagePath]);

    if (error) {
      throw error;
    }

    return { success: true };

  } catch (error) {
    console.error('Error deleting event image:', error);
    return {
      error: error?.message || 'Failed to delete image',
      success: false
    };
  }
};

/**
 * Get public URL for event image
 * @param {string} imagePath - Path to the image in storage
 * @returns {string} Public URL for the image
 */
export const getEventImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  const { data: { publicUrl } } = supabase?.storage?.from('event-images')?.getPublicUrl(imagePath);
    
  return publicUrl;
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @returns {{isValid: boolean, error?: string}}
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes?.includes(file?.type)) {
    return { 
      isValid: false, 
      error: 'Invalid file type. Please upload JPEG, PNG, or WebP images.' 
    };
  }

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file?.size > maxSize) {
    return { 
      isValid: false, 
      error: 'File size too large. Please upload images smaller than 10MB.' 
    };
  }

  return { isValid: true };
};